import { DDZ_INVITE_PATH, readInviteContext, readInviteToken, inviteLocation } from "./DDZInvite";
import { roomInviteHomePath, readRoomInvite, ROOM_INVITE_REGISTRY } from "./RoomInvite";

export const DDZ_INVITE_BRIDGE_STORAGE_KEY = "ddzRoomInviteBridge:v1";
const TTL = 7 * 24 * 60 * 60 * 1000;
const MAX_DELIVERIES = 32;
const PUBLIC_FIELDS = new Set(["invite_token", "aid", "f", "c", "p"]);
const validId = id => typeof id === "string" && id.length > 0 && id.length <= 200 && !/[\u0000-\u001f\u007f]/.test(id);
const installed = new WeakMap();

// The APK supplies a public share URL, never an account token or a game Ticket.
export function ddzInviteCandidatePath(candidate, hallOrigin) {
  if (typeof candidate !== "string" || !candidate || candidate.length > 4096) return null;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "https:" || url.origin !== hallOrigin || url.username || url.password || url.href.includes("#") ||
        ![DDZ_INVITE_PATH, "/home"].includes(url.pathname)) return null;
    for (const key of url.searchParams.keys()) {
      if (!PUBLIC_FIELDS.has(key) || url.searchParams.getAll(key).length !== 1) return null;
    }
    const context = readInviteContext(url.search);
    if (!context) return null;
    readInviteToken(context.inviteToken);
    // Invalid attribution must not silently become a different accepted invitation.
    for (const key of ["aid", "f", "c", "p"]) {
      if (url.searchParams.has(key) && context.attribution[key] !== url.searchParams.get(key)) return null;
    }
    return inviteLocation(context.inviteToken, context.attribution);
  } catch (_) { return null; }
}

// Generic rooms use the Egypt registry. A token-bearing URL can never fall back to this protocol.
function inviteCandidatePath(candidate, hallOrigin, registry) {
  if (typeof candidate !== "string" || !candidate || candidate.length > 4096) return null;
  try {
    const url = new URL(candidate);
    if (url.searchParams.has("invite_token")) return ddzInviteCandidatePath(candidate, hallOrigin);
    if (url.protocol !== "https:" || url.origin !== hallOrigin || url.username || url.password ||
        url.href.includes("#") || url.pathname !== "/home") return null;
    return roomInviteHomePath(url.search, registry);
  } catch (_) { return null; }
}

export function createDDZInviteBridge({ hallOrigin, storage, now = () => Date.now(),
  sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`, registry = ROOM_INVITE_REGISTRY }) {
  const listeners = new Set();
  const routedPaths = new Set();
  const ack = (deliveryId, status) => ({ version: 1, deliveryId, status });
  const publish = () => listeners.forEach(listener => {
    try { listener(api.pending()); } catch (_) { /* A UI listener cannot revoke a stored delivery. */ }
  });
  const canonicalSavedPath = path => typeof path === "string" &&
    (path.startsWith(`${DDZ_INVITE_PATH}?`) || path.startsWith("/home?")) &&
    inviteCandidatePath(`${hallOrigin}${path}`, hallOrigin, registry) === path;
  const read = () => {
    try {
      const saved = JSON.parse(storage.getItem(DDZ_INVITE_BRIDGE_STORAGE_KEY) || "null");
      const at = now();
      const deliveries = saved && saved.version === 1 && Array.isArray(saved.deliveries) ? saved.deliveries.filter(item =>
        item && validId(item.deliveryId) && canonicalSavedPath(item.path) && Number.isFinite(item.acceptedAt) &&
        item.acceptedAt <= at && at - item.acceptedAt <= TTL).slice(-MAX_DELIVERIES).map(item => ({
          deliveryId: item.deliveryId, path: item.path, acceptedAt: item.acceptedAt,
        })) : [];
      const stored = saved && saved.pending;
      const matching = stored && deliveries.find(item => item.deliveryId === stored.deliveryId && item.path === stored.path);
      const pending = matching ? {
        deliveryId: matching.deliveryId, path: matching.path,
        phase: stored.phase === "routing" && stored.routingSession === sessionId ? "routing" : "pending",
        ...(stored.phase === "routing" && stored.routingSession === sessionId ? { routingSession: sessionId } : {}),
      } : null;
      return { version: 1, deliveries, pending };
    } catch (_) { return null; }
  };
  const write = state => {
    try { storage.setItem(DDZ_INVITE_BRIDGE_STORAGE_KEY, JSON.stringify(state)); return true; }
    catch (_) { return false; }
  };
  const api = {
    version: 1,
    receive(envelope) {
      const id = envelope && envelope.deliveryId;
      if (!envelope || envelope.version !== 1 || !validId(id)) return ack(validId(id) ? id : "", "unsupported");
      const path = inviteCandidatePath(envelope.candidate, hallOrigin, registry);
      if (!path) return ack(id, "unsupported");
      const state = read();
      if (!state) return ack(id, "not_ready");
      const existing = state.deliveries.find(item => item.deliveryId === id);
      if (existing) return ack(id, existing.path === path ? "accepted" : "unsupported");
      const samePending = state.pending && state.pending.path === path;
      state.deliveries.push({ deliveryId: id, path, acceptedAt: now() });
      state.deliveries = state.deliveries.slice(-MAX_DELIVERIES);
      if (!samePending) state.pending = { deliveryId: id, path, phase: "pending" };
      else state.pending.deliveryId = id;
      if (!write(state)) return ack(id, "not_ready");
      publish();
      return ack(id, "accepted");
    },
    status(deliveryId) {
      const state = read();
      return ack(validId(deliveryId) ? deliveryId : "", validId(deliveryId) && state &&
        state.deliveries.some(item => item.deliveryId === deliveryId) ? "accepted" : "not_ready");
    },
    pending() {
      const state = read();
      const pending = state && state.pending;
      if (!pending) return null;
      const kind = pending.path.startsWith(`${DDZ_INVITE_PATH}?`) ? "ddz" : "room";
      const search = pending.path.slice(pending.path.indexOf("?"));
      const context = kind === "ddz" ? readInviteContext(search) : readRoomInvite(search, registry);
      return { deliveryId: pending.deliveryId, path: pending.path, phase: pending.phase, kind, ...context };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    markRouted(deliveryId) {
      const state = read();
      if (!state || !state.pending || state.pending.deliveryId !== deliveryId) return false;
      if (state.pending.phase === "routing") return true;
      state.pending = { ...state.pending, phase: "routing", routingSession: sessionId };
      if (!write(state)) return false;
      routedPaths.add(state.pending.path);
      if (routedPaths.size > MAX_DELIVERIES) routedPaths.delete(routedPaths.values().next().value);
      publish();
      return true;
    },
    isSupersededPath(path) {
      const state = read();
      return routedPaths.has(path) && !!(state && state.pending && state.pending.path !== path);
    },
    retryRoute(deliveryId) {
      const state = read();
      if (!state || !state.pending || state.pending.deliveryId !== deliveryId) return false;
      state.pending = { deliveryId, path: state.pending.path, phase: "pending" };
      if (!write(state)) return false;
      // Retry on a later navigation/readiness change, not in an immediate effect loop.
      return true;
    },
    finish(inviteToken, expectedPath) {
      const state = read();
      const pending = state && state.pending;
      if (!pending || !pending.path.startsWith(`${DDZ_INVITE_PATH}?`) ||
          (expectedPath && pending.path !== expectedPath)) return false;
      const context = readInviteContext(pending.path.slice(pending.path.indexOf("?")));
      if (context.inviteToken !== inviteToken) return false;
      state.pending = null;
      if (!write(state)) return false;
      publish();
      return true;
    },
    finishRoom(deliveryId, expectedPath) {
      const state = read();
      const pending = state && state.pending;
      if (!pending || !pending.path.startsWith("/home?") || pending.deliveryId !== deliveryId ||
          (expectedPath && pending.path !== expectedPath)) return false;
      state.pending = null;
      if (!write(state)) return false;
      publish();
      return true;
    },
  };
  return api;
}

// Installed on the first provider render, before persisted login data is available.
// The public API remains available while routes/components mount and unmount.
export function installDDZInviteBridge(host, { registry = ROOM_INVITE_REGISTRY } = {}) {
  if (installed.has(host)) return installed.get(host);
  const bridge = createDDZInviteBridge({
    hallOrigin: host.location.origin,
    registry,
    storage: {
      getItem: key => host.localStorage.getItem(key),
      setItem: (key, value) => host.localStorage.setItem(key, value),
    },
  });
  host.RoomInviteBridge = { version: 1, receive: bridge.receive, status: bridge.status };
  installed.set(host, bridge);
  return bridge;
}

// Routing hooks share the installed receiver; only the provider owns the public native API.
export function getInstalledRoomInviteBridge(host) { return installed.get(host) || null; }
